/**
 * Unified Backend Configuration
 * Responsibility: Resolving API and Socket endpoints across environments.
 */
const getBackendUrl = () => {
  /**
   * 🛡️ PRODUCTION PRIORITY
   * In production, we explicitly prioritize the current window origin
   * to ensure no cross-origin confusion in multi-tenant or mirrored deployments.
   */
  if (import.meta.env.PROD) {
    return window.location.origin;
  }

  // Development / Preview environment handling
  const { hostname, protocol } = window.location;

  if (hostname.endsWith(".stackblitz.io")) {
    return `${protocol}//${hostname.replace(".stackblitz.io", "-3001.stackblitz.io")}`;
  }

  return "http://localhost:3000";
};

export const API_BASE_URL =
  import.meta.env.PROD && import.meta.env.VITE_API_BASE_URL
    ? import.meta.env.VITE_API_BASE_URL
    : getBackendUrl();
export const LOCAL_GATEWAY_URL =
  import.meta.env.VITE_GATEWAY_URL || "http://localhost:3000";
export const LOCAL_AUTH_SERVICE_URL =
  import.meta.env.VITE_AUTH_SERVICE_URL || "http://localhost:3002/api/v1";
export const LOCAL_INGESTION_SERVICE_URL =
  import.meta.env.VITE_INGESTION_SERVICE_URL || "http://localhost:3003/api/v1";
export const LOCAL_AI_SERVICE_URL =
  import.meta.env.VITE_AI_SERVICE_URL || "http://localhost:3004/api/v1";
export const LOCAL_REALTIME_SERVICE_URL =
  import.meta.env.VITE_REALTIME_SERVICE_URL || "http://localhost:3005";

export const getLocalDevBaseUrl = (requestPath) => {
  const path = requestPath?.startsWith("/")
    ? requestPath
    : `/${requestPath || ""}`;

  if (path.startsWith("/auth") || path.startsWith("/compliance")) {
    return LOCAL_GATEWAY_URL;
  }

  if (path.startsWith("/ingest") || path.startsWith("/api/v1/ingest")) {
    return LOCAL_INGESTION_SERVICE_URL;
  }

  if (path.startsWith("/ai") || path.startsWith("/api/v1/ai")) {
    return LOCAL_AI_SERVICE_URL;
  }

  if (path.startsWith("/analytics") || path.startsWith("/api/v1/analytics")) {
    return LOCAL_GATEWAY_URL;
  }

  if (path.startsWith("/alerts") || path.startsWith("/api/v1/alerts")) {
    return `${LOCAL_GATEWAY_URL}/api/v1`;
  }

  if (
    path.startsWith("/admin") ||
    path.startsWith("/auditor") ||
    path.startsWith("/founder") ||
    path.startsWith("/api/v1/admin") ||
    path.startsWith("/api/v1/auditor") ||
    path.startsWith("/api/v1/founder")
  ) {
    return LOCAL_AUTH_SERVICE_URL;
  }

  if (
    path.startsWith("/demographics") ||
    path.startsWith("/api/v1/demographics")
  ) {
    return LOCAL_AUTH_SERVICE_URL;
  }

  if (path.startsWith("/users") || path.startsWith("/api/v1/users")) {
    // LOCAL_GATEWAY_URL does not include /api/v1 (unlike
    // LOCAL_AUTH_SERVICE_URL, which does) — call sites here pass bare
    // paths like "/users?role=patient", so without appending /api/v1
    // here the request misses every Gateway proxy route entirely and
    // falls through to the SPA catch-all, returning index.html instead
    // of JSON. This was the root cause of "no user found" in
    // AdminDashboard's User Management tab.
    return `${LOCAL_GATEWAY_URL}/api/v1`;
  }

  console.warn(
    `[config] getLocalDevBaseUrl: no route prefix matched for "${path}" — falling back to auth service. This may be unintentional; add an explicit prefix rule if this path is expected to hit a different service.`,
  );
  return LOCAL_AUTH_SERVICE_URL;
};

export const SOCKET_URL =
  import.meta.env.PROD && import.meta.env.VITE_SOCKET_URL
    ? import.meta.env.VITE_SOCKET_URL
    : import.meta.env.DEV
      ? LOCAL_REALTIME_SERVICE_URL
      : getBackendUrl();
